<template>
	<div
		v-if="questions.length > 0 || compProps.dataobject.id === workspacesStore.selectedNodeId"
		class="questions-wrapper"
	>
		<v-card
			rounded="lg"
			:class="{ questionBorder: compProps.dataobject.id === workspacesStore.selectedNodeId }"
			class="mb-1 mx-1"
			style="user-select: none"
			elevation="2"
			@click="zoomTo"
			@dblclick="select"
		>
			<v-card-title class="multiline ml-2">
				{{
					symbolsStore.term({
						id: templateDataobject?.refersTo || "",
						terminology: templateDataobject?.terminology,
					}) ||
					symbolsStore.term({
						id: conceptDataobject?.refersTo || "",
						terminology: conceptDataobject?.terminology,
					})
				}}:
				<question-dataobject-identifier
					:dataobject="compProps.dataobject"
					:concept-count="compProps.conceptCount"
					:concept-index="compProps.conceptIndex"
				/>
			</v-card-title>
		</v-card>

		<v-expansion-panels
			v-model="panel"
			static
			multiple
			:variant="questions.length > 1 ? 'accordion' : 'default'"
			class="panels-container"
		>
			<question-panel
				v-for="(item, i) in questions"
				:key="item.instanceTask.id"
				:title="item.title[domainStore.languagePreference]"
				:subtitle="item.subtitle[domainStore.languagePreference]"
				:noGPTRephrasing="item.noGPTRephrasing"
				:playButton="item.playButton"
				:showUnknownButton="item.showUnknownButton"
				:instance-task="item.instanceTask"
				:index="i"
			/>
		</v-expansion-panels>
	</div>
</template>

<script setup lang="ts">
import { ref, PropType, computed, watch, onMounted } from "vue";
import type { InstanceDataobject } from "@lr-notitia/notitia-operator/schemas";
import { useGraphStore } from "../../../store/graph";
import { useQuestionsStore } from "../../../store/questions";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useSymbolsStore } from "../../../store/symbols";
import { useDomainStore } from "../../../store/domain";
import QuestionPanel from "./QuestionPanel.vue";
import QuestionDataobjectIdentifier from "./QuestionDataobjectIdentifier.vue";

const graphStore = useGraphStore();
const questionsStore = useQuestionsStore();
const workspacesStore = useInstanceWorkspacesStore();
const symbolsStore = useSymbolsStore();
const domainStore = useDomainStore();

const compProps = defineProps({
	dataobject: {
		type: Object as PropType<InstanceDataobject>,
		required: true,
	},
	dataobjectsInWorkspaceCount: {
		type: Number,
		required: true,
	},
	autoExpand: {
		type: Boolean,
		required: true,
	},
	conceptCount: {
		type: Number,
		default: 1,
	},
	conceptIndex: {
		type: Number,
		default: 1,
	},
});

const panel = ref();

const templateDataobject = computed(() => {
	return graphStore.template.dataobject.byId(compProps.dataobject.template);
});
const conceptDataobject = computed(() => {
	return graphStore.concept.dataobject.byId(compProps.dataobject.concept);
});

const questions = computed(() => {
	let composedDataobjectIds = graphStore.instance.dataobject.composedDataobjectsIds(compProps.dataobject.id);
	let hasADataobjectIds = innerHasADataobjectIds(compProps.dataobject.id);
	let q = questionsStore.tab === "open" ? questionsStore.unansweredQuestions : questionsStore.answeredQuestions;
	return q.filter((question) => {
		return (
			composedDataobjectIds.includes(question.instanceTask.inputDataobject) ||
			composedDataobjectIds.includes(question.instanceTask.outputDataobject) ||
			hasADataobjectIds.includes(question.instanceTask.inputDataobject) ||
			hasADataobjectIds.includes(question.instanceTask.outputDataobject)
		);
	});
});
const questionIds = computed(() => questions.value.map((q) => q.instanceTask.id));

function innerHasADataobjectIds(id: string): string[] {
	function isGraphlyNode(id: string) {
		let instance = graphStore.instance.dataobject.byId(id) as InstanceDataobject;
		return !!instance.template;
	}
	let result: any = [];
	const dataobject = graphStore.instance.dataobject.byId(id) as InstanceDataobject;
	if (!dataobject) return result;
	dataobject.hasA.forEach((hasAId) => {
		if (isGraphlyNode(hasAId)) return [];
		result.push(hasAId);
		result = result.concat(innerHasADataobjectIds(hasAId));
	});
	return result;
}

function handleExpand() {
	if (!compProps.autoExpand) return;
	let newExpanded: string[] = [];
	function keepCurrentQuestionExpanded() {
		if (!questionsStore.currentQuestion) return;
		if (!newExpanded.includes(questionsStore.currentQuestion)) {
			newExpanded.push(questionsStore.currentQuestion);
		}
	}
	function keepLatestAnsweredIndexExpanded() {
		if (questionsStore.latestAnsweredQuestion.dataobjectId !== compProps.dataobject.id) return;
		if (questionsStore.latestAnsweredQuestion.index === -1) return;

		let newQuestionAtIndex = questions.value[questionsStore.latestAnsweredQuestion.index]?.instanceTask?.id;
		if (!newQuestionAtIndex) return;
		if (!newExpanded.includes(newQuestionAtIndex)) {
			newExpanded.push(newQuestionAtIndex);
		}
	}
	function openFirstQuestion() {
		let firstQuestionId = questions.value?.[0]?.instanceTask?.id;
		if (!firstQuestionId) return;
		if (!newExpanded.includes(firstQuestionId)) {
			newExpanded.push(firstQuestionId);
		}
	}

	if (panel.value && typeof panel.value === "string") newExpanded = [panel.value];
	if (panel.value && Array.isArray(panel.value)) newExpanded = panel.value;
	if (!panel.value) newExpanded = [];
	newExpanded = newExpanded.filter((id) => questions.value.find((q) => q.instanceTask.id === id));

	keepCurrentQuestionExpanded();
	keepLatestAnsweredIndexExpanded();
	openFirstQuestion();

	if (newExpanded.length === 0) panel.value = undefined;
	if (newExpanded.length === 1) panel.value = newExpanded[0];
	if (newExpanded.length > 1) panel.value = newExpanded;
}

function zoomTo() {
	questionsStore.zoomToId = compProps.dataobject.id;
}
function select(e: any) {
	if (workspacesStore.selectedNodeId === compProps.dataobject.id) workspacesStore.selectedNodeId = "";
	else workspacesStore.selectedNodeId = compProps.dataobject.id;
}

watch(
	() => questionIds.value,
	(newIds, oldIds) => {
		if (newIds.length !== oldIds.length || newIds.some((id, index) => id !== oldIds[index])) handleExpand();
	},
);

onMounted(() => {
	handleExpand();
});
</script>

<style scoped lang="scss">
.questions-wrapper {
	padding-bottom: 4px;
}

.panels-container {
	margin-left: 12px;
	width: calc(100% - 12px - 4px);
}

.questionlist-enter-active,
.questionlist-leave-active {
	transition: all 0.5s ease;
}
.questionlist-enter-from,
.questionlist-leave-to {
	opacity: 0;
	transform: translateX(30px);
}

.questionBorder {
	border: 2px solid skyblue;
}

.multiline {
	white-space: pre-wrap;
}
</style>
